import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useSiteContext } from '@zooplus/commons-lang';
import { H2, P1, Skeleton } from '@zooplus/ui-components';

import { MetricsProvider } from '../../contexts/MetricsContext';
import { useTranslation } from '../../hooks/use-translations';
import { useMetrics } from '../../hooks/useMetrics';
import useToken from '../../hooks/useToken';
import { LastEligibleOrderInfo } from '../../model/order';
import { ProductInfo } from '../../model/product';
import { Brand } from '../../model/site';
import { SubscriptionInfo } from '../../model/subscriptionProducts';
import { CentralHubService } from '../../services/CentralHubService';
import { Benefits } from '../benefits';
import { ContentBanner } from '../common/content-banner/ContentBanner';
import { DidYouKnow } from '../did-you-know/DidYouKnow';
import { EligibleBrands } from '../eligible-brands';
import { Examples } from '../examples';
import { Faq } from '../faq';
import { HowItWorks } from '../how-it-works';
import { MostSubscribed } from '../most-subscribed/MostSubscribed';
import { PdpArticle } from '../pdp-article/PdpArticle';
import { PreviouslyOrdered } from '../previously-ordered/PreviouslyOrdered';
import { Reviews } from '../reviews';
import { UpcomingSubscriptions } from '../upcoming-subscriptions';

import styles from './CentralHub.module.scss';

const CentralHubInner = ({ metrics }: { metrics: ReturnType<typeof useMetrics> }) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { site, language } = useSiteContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingSiteInfo, setLoadingSiteInfo] = useState<boolean>(true);
  const [autoshipmentInfo, setAutoshipmentInfo] = useState<SubscriptionInfo | undefined>(undefined);
  const [pdpInfo, setPdpInfo] = useState<ProductInfo | undefined>(undefined);
  const [lastEligibleOrder, setLastEligibleOrder] = useState<LastEligibleOrderInfo | undefined>(
    undefined,
  );
  const [brands, setBrands] = useState<Brand[] | undefined>(undefined);
  const [firstOrderDiscount, setFirstOrderDiscount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (token && !lastEligibleOrder) {
      CentralHubService(token)
        .getLastEligibleOrder(site.domain)
        .then((response) => {
          if (response.url) {
            setLastEligibleOrder(response);
          }
        })
        .catch(() => {
          metrics.renderError('EndpointError');
        });
    }
  }, [token]);

  useEffect(() => {
    const shopIdentifier = new URLSearchParams(location.search).get('activeVariant');
    if (shopIdentifier)
      CentralHubService(undefined)
        .getPdpArticleInformation(shopIdentifier, language)
        .then((response) => {
          if (response.articleShopId) {
            setPdpInfo(response);
          }
        })
        .catch(() => {
          metrics.renderError('EndpointError');
        });
  }, []);

  useEffect(() => {
    CentralHubService(undefined)
      .getSiteInfo(site.domain)
      .then((response) => {
        if (response) {
          setBrands(response.brands);
          setFirstOrderDiscount(response.firstOrderDiscount);
        }
      })
      .catch(() => {
        metrics.renderError('EndpointError');
      })
      .finally(() => setLoadingSiteInfo(false));
  }, []);

  useEffect(() => {
    CentralHubService(token)
      .getSubscriptions(site.domain)
      .then((response) => {
        if (response.upcomingAutoshipments) {
          setAutoshipmentInfo(response);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!loading && !loadingSiteInfo) {
      metrics.renderSuccess();
    }
  }, [loading, loadingSiteInfo]);

  const comboSection = () => {
    if (!pdpInfo && !lastEligibleOrder) return <Benefits firstOrderDiscount={firstOrderDiscount} />;
    else if (lastEligibleOrder && pdpInfo) {
      return (
        <>
          <Benefits firstOrderDiscount={firstOrderDiscount} />
          <div className={styles.comboSection}>
            <PdpArticle articleInfo={pdpInfo} />
            <PreviouslyOrdered lastEligibleOrderInfo={lastEligibleOrder} />
          </div>
        </>
      );
    } else
      return (
        <>
          <div className={styles.comboSection}>
            <Benefits benefitsComboStyles={true} firstOrderDiscount={firstOrderDiscount} />
            {lastEligibleOrder ? (
              <PreviouslyOrdered lastEligibleOrderInfo={lastEligibleOrder} />
            ) : (
              <PdpArticle articleInfo={pdpInfo!} />
            )}
          </div>
        </>
      );
  };

  const centralHubTitle = (
    <section>
      <H2>{t('title')}</H2>
      <P1 bold className={styles.sentence}>
        {t('description')}
      </P1>
    </section>
  );

  const campaignTextContainer = 'campaign-info-text';

  const content = () => {
    if (loading || loadingSiteInfo) return <Skeleton className={styles.skeleton} height={'500'} />;
    return autoshipmentInfo?.upcomingAutoshipments ? (
      <>
        <UpcomingSubscriptions subscriptions={autoshipmentInfo} />
        <MostSubscribed />
        <DidYouKnow />
        <Faq />
        <EligibleBrands brands={brands || []} />
      </>
    ) : (
      <>
        <ContentBanner productGroup={33333} zone={'M2'} textContainerId={campaignTextContainer} />
        <HowItWorks />
        {comboSection()}
        <Faq />
        <Reviews />
        <Examples />
        <EligibleBrands brands={brands || []} />
        <MostSubscribed />
        <div id={campaignTextContainer} />
      </>
    );
  };

  return (
    <div className={styles.root}>
      {centralHubTitle}
      {content()}
    </div>
  );
};

const CentralHubWithMetrics = () => {
  const metrics = useMetrics('central-hub');

  useEffect(() => {
    metrics.renderInit();
  }, []);

  return (
    <ErrorBoundary fallback={<></>} onError={() => metrics.renderError('RenderError')}>
      <CentralHubInner metrics={metrics} />
    </ErrorBoundary>
  );
};

export const CentralHub = () => (
  <MetricsProvider>
    <CentralHubWithMetrics />
  </MetricsProvider>
);
